/**
 * CR-11 payout accounts and CR-12 payout requests (FR-FIN-006..009).
 *
 * Two things this screen deliberately does not do.
 *
 * It does not compute the amount for "Withdraw available balance". That
 * sends `amountMinor: null` and the database decides under the lock, so
 * the figure the creator gets is the one that was really there and not the
 * one this page happened to be showing. A screen that filled the box with
 * its own balance would look identical and be wrong every time a payout
 * landed while the page was open.
 *
 * And it does not decide whether the button should work. `cancellable`
 * comes from the same predicate the cancel CAS uses, so the button appears
 * exactly when pressing it would succeed - rather than from a rule written
 * twice, which is a rule that will disagree with itself.
 *
 * The confirmation panel is FR-FIN-007's list, in its order: gross, each
 * deduction separately, net, the masked destination, and what happens
 * next. Both deduction schedules are zero today and the rows still appear,
 * because a creator who sees "Fees 0.00" once knows where a fee would show
 * up if there ever were one.
 */
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Badge, Button, Card, Eyebrow, Money, PageHeader, StateBlock } from "@ui/primitives";
import { TextInput, Select, FormError } from "@ui/forms";
import { useToast } from "@app/shared/Toasts";
import { fromMinorString, type CurrencyCode } from "@contracts/money";
import { fontSize, space } from "@design/index";
import { api, type ApiFieldErrors } from "@app/shared/api";
import { CardListSkeleton, RibbonSkeleton } from "@app/shared/Skeletons";

interface RouteField { name: string; label: string; hint: string }
interface PayoutRoute {
  route: string; country: string; currency: string; label: string; fields: RouteField[];
}
interface Account {
  id: string; country: string; route: string; currency: string;
  beneficiary_name: string; masked_display: string; status: string;
  status_reason: string | null; verified_at: string | null; created_at: string;
}
interface Balance {
  currency: string; available_minor: string; reserved_minor: string;
  in_transit_minor: string; held_minor: string; pending_minor: string;
}
interface Request {
  id: string; reference: string; currency: string;
  gross_minor: string; fee_minor: string; tax_minor: string; net_minor: string;
  destination_masked: string; review_state: string; allocation_state: string;
  decision_reason: string | null; decided_at: string | null;
  claimed: boolean; cancellable: boolean; lots: number;
  created_at: string; updated_at: string;
}

const Amount = ({ minor, currency, emphasis = false }: {
  minor: string; currency: string; emphasis?: boolean;
}) => <Money value={fromMinorString(minor, currency as CurrencyCode)} emphasis={emphasis} />;

/** What each review state means to the person waiting on the money. */
const REVIEW: Record<string, { tone: "ok" | "warn" | "process" | "neutral"; says: string }> = {
  SUBMITTED: { tone: "process", says: "Waiting for finance to pick it up" },
  UNDER_REVIEW: { tone: "process", says: "Finance is looking at it" },
  AWAITING_ADDITIONAL_APPROVAL: { tone: "process", says: "Waiting on a second approver" },
  APPROVED: { tone: "ok", says: "Approved and on its way" },
  REJECTED: { tone: "warn", says: "Not approved" },
  CANCELLED: { tone: "neutral", says: "Cancelled" },
};

/** And what has happened to the money, which is a different question. */
const ALLOCATION: Record<string, string> = {
  RESERVED: "held aside from your available balance",
  RELEASED: "back in your available balance",
  IN_TRANSIT: "sent to your bank",
  PAID: "paid",
  RECOVERED: "returned to your balance",
  PARTIALLY_RECOVERED: "partly returned to your balance",
};

const when = (v: string | null) => {
  if (!v) return "-";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "-" : d.toISOString().slice(0, 16).replace("T", " ");
};

export function PayoutsScreen() {
  const [routes, setRoutes] = useState<PayoutRoute[]>([]);
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [balances, setBalances] = useState<Balance[]>([]);
  const [requests, setRequests] = useState<Request[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const load = useCallback(async () => {
    const [r, a, b, q] = await Promise.all([
      api.get<{ routes: PayoutRoute[] }>("/payout-routes"),
      api.get<{ accounts: Account[] }>("/payout-accounts"),
      api.get<{ balances: Balance[] }>("/earnings"),
      api.get<{ requests: Request[] }>("/payout-requests"),
    ]);
    if (r.ok) setRoutes(r.data.routes);
    if (a.ok) setAccounts(a.data.accounts); else { setError(a.message); setAccounts([]); }
    if (b.ok) setBalances(b.data.balances);
    if (q.ok) setRequests(q.data.requests); else setRequests([]);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const verified = useMemo(
    () => (accounts ?? []).filter((a) => a.status === "VERIFIED"), [accounts]);

  return (
    <>
      <PageHeader
        eyebrow="Payouts"
        title="Get paid"
        description="Withdraw what is available to a bank account we have checked. The amount is held aside while finance reviews it - it stays yours until it leaves."
      />

      {error ? <div style={{ marginBottom: space(3) }}>
        <StateBlock kind="error" title="Could not load your payouts" detail={error} />
      </div> : null}

      <div style={{ display: "grid", gap: space(4) }}>
        <RequestPanel
          balances={balances} accounts={verified}
          onDone={(m, ok = true) => { (ok ? toast.ok : toast.error)(m); void load(); }} />
        <HistoryPanel
          requests={requests}
          onDone={(m, ok = true) => { (ok ? toast.ok : toast.error)(m); void load(); }} />
        <AccountsPanel
          accounts={accounts} routes={routes}
          onDone={(m, ok = true) => { (ok ? toast.ok : toast.error)(m); void load(); }} />
      </div>
    </>
  );
}

/* ------------------------------------------------------- request (CR-12) */

function RequestPanel({ balances, accounts, onDone }: {
  balances: Balance[]; accounts: Account[]; onDone: (m: string, ok?: boolean) => void;
}) {
  const [accountId, setAccountId] = useState("");
  const [whole, setWhole] = useState(true);
  const [amount, setAmount] = useState("");
  const [fields, setFields] = useState<ApiFieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Minted once per intent, not per submit, so a double-click or a retry
  // after a dropped response is the same request rather than a second one.
  const [key, setKey] = useState(() => crypto.randomUUID());

  const account = accounts.find((a) => a.id === accountId);
  const balance = balances.find((b) => b.currency === account?.currency);
  const availableMinor = balance?.available_minor ?? "0";

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!account) return;
    setBusy(true); setFields({}); setFormError(null);

    const minor = whole ? null : Math.round(Number(amount) * 100);
    if (!whole && (!Number.isSafeInteger(minor) || (minor ?? 0) <= 0)) {
      setBusy(false);
      setFields({ amountMinor: "Enter an amount." });
      return;
    }

    const res = await api.post<{ reference: string; net_minor: string; replayed: boolean }>(
      "/payout-requests",
      { accountVersionId: account.id, currency: account.currency,
        amountMinor: minor, idempotencyKey: key });
    setBusy(false);

    if (!res.ok) {
      setFields(res.fields ?? {});
      setFormError(res.fields ? null : res.message);
      return;
    }
    setKey(crypto.randomUUID());
    setAmount("");
    onDone(res.data.replayed
      ? `That payout already existed (${res.data.reference}). Nothing further was reserved.`
      : `Payout ${res.data.reference} requested. `
        + `${(Number(res.data.net_minor) / 100).toFixed(2)} ${account.currency} `
        + "will be sent once finance approves it.");
  }

  if (accounts.length === 0) {
    return (
      <Card padding={18}>
        <Eyebrow>Request a payout</Eyebrow>
        <p style={{ margin: `${space(2)}px 0 0`, color: "var(--muted)",
                    fontSize: fontSize.body, lineHeight: 1.6 }}>
          Add a bank account below first. We check a new account before the
          first payout to it, so this is worth doing before you need it.
        </p>
      </Card>
    );
  }

  const gross = whole ? availableMinor : String(Math.round(Number(amount || 0) * 100));
  const ccy = account?.currency ?? "";

  return (
    <Card padding={18}>
      <Eyebrow>Request a payout</Eyebrow>
      <form onSubmit={submit} style={{ display: "grid", gap: space(3), marginTop: space(3) }}>
        <Select label="To" required value={accountId}
          onChange={(e) => setAccountId(e.currentTarget.value)}
          {...(fields["accountVersionId"] ? { error: fields["accountVersionId"] } : {})}>
          <option value="">Choose a verified account</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.masked_display} - {a.beneficiary_name}
            </option>
          ))}
        </Select>

        {account ? (
          <>
            <div style={{ display: "flex", gap: space(3), alignItems: "center",
                          flexWrap: "wrap" }}>
              <label style={{ display: "flex", gap: 8, alignItems: "center",
                              fontSize: fontSize.body, cursor: "pointer" }}>
                <input type="radio" checked={whole} onChange={() => setWhole(true)} />
                Withdraw available balance
              </label>
              <label style={{ display: "flex", gap: 8, alignItems: "center",
                              fontSize: fontSize.body, cursor: "pointer" }}>
                <input type="radio" checked={!whole} onChange={() => setWhole(false)} />
                A specific amount
              </label>
            </div>

            {whole ? null : (
              <TextInput label={`Amount (${ccy})`} required inputMode="decimal"
                value={amount} onChange={(e) => setAmount(e.currentTarget.value)}
                hint={`You have ${(Number(availableMinor) / 100).toFixed(2)} ${ccy} available.`}
                {...(fields["amountMinor"] ? { error: fields["amountMinor"] } : {})} />
            )}

            {/* FR-FIN-007's list, before the button that commits to it. */}
            <div style={{ display: "grid", gap: 6, padding: 14, borderRadius: 10,
                          background: "var(--surface-2)", fontSize: fontSize.label }}>
              <Line label="You are withdrawing"
                value={<Amount minor={gross} currency={ccy} emphasis />} />
              <Line label="Payout fee" value={<Amount minor="0" currency={ccy} />} />
              <Line label="Withholding" value={<Amount minor="0" currency={ccy} />} />
              <Line label="Sent to your bank"
                value={<Amount minor={gross} currency={ccy} emphasis />} />
              <Line label="Destination" value={<span>{account.masked_display}</span>} />
              <p style={{ margin: "8px 0 0", color: "var(--muted)", lineHeight: 1.6 }}>
                {whole
                  ? "The exact amount is worked out when you confirm, so it matches your "
                    + "balance at that moment rather than what this page last loaded. "
                  : ""}
                Finance reviews every payout before it is sent. Until then the amount is
                held aside and you can cancel it here.
              </p>
            </div>

            {formError ? <FormError message={formError} /> : null}
            <div>
              <Button type="submit" disabled={busy || (!whole && !amount)}>
                {busy ? "Requesting…" : "Request payout"}
              </Button>
            </div>
          </>
        ) : null}
      </form>
    </Card>
  );
}

const Line = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: space(3) }}>
    <span style={{ color: "var(--muted)" }}>{label}</span>
    <span>{value}</span>
  </div>
);

/* ------------------------------------------------------- history (CR-12) */

function HistoryPanel({ requests, onDone }: {
  requests: Request[] | null; onDone: (m: string, ok?: boolean) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);

  async function cancel(r: Request) {
    setBusy(r.id);
    const res = await api.post(`/payout-requests/${r.id}/cancel`, { reason: null });
    setBusy(null);
    onDone(res.ok
      ? `Payout ${r.reference} cancelled. The amount is back in your available balance.`
      : res.message, res.ok);
  }

  return (
    <Card padding={18}>
      <Eyebrow>Your payouts</Eyebrow>
      <div style={{ marginTop: space(3) }}>
        {requests === null ? <RibbonSkeleton count={3} />
          : requests.length === 0 ? (
            <StateBlock kind="empty" title="No payouts yet"
              detail="Once you request one it appears here with its reference and status." />
          ) : (
            <div style={{ display: "grid", gap: space(3) }}>
              {requests.map((r) => {
                const state = REVIEW[r.review_state]
                  ?? { tone: "neutral" as const, says: r.review_state };
                return (
                  <div key={r.id} style={{ display: "grid", gap: 8, padding: 14,
                                           borderRadius: 10, border: "1px solid var(--line)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between",
                                  alignItems: "baseline", gap: space(2), flexWrap: "wrap" }}>
                      <strong style={{ fontVariantNumeric: "tabular-nums" }}>{r.reference}</strong>
                      <Badge tone={state.tone}>{state.says}</Badge>
                    </div>

                    <div style={{ display: "grid", gap: 4, fontSize: fontSize.label }}>
                      <Line label="Withdrawn"
                        value={<Amount minor={r.gross_minor} currency={r.currency} emphasis />} />
                      {r.fee_minor !== "0" ? (
                        <Line label="Payout fee"
                          value={<Amount minor={r.fee_minor} currency={r.currency} />} />
                      ) : null}
                      {r.tax_minor !== "0" ? (
                        <Line label="Withholding"
                          value={<Amount minor={r.tax_minor} currency={r.currency} />} />
                      ) : null}
                      <Line label="To your bank"
                        value={<Amount minor={r.net_minor} currency={r.currency} />} />
                      <Line label="Destination" value={<span>{r.destination_masked}</span>} />
                      <Line label="Requested" value={<span>{when(r.created_at)}</span>} />
                    </div>

                    <p style={{ margin: 0, color: "var(--muted)",
                                fontSize: fontSize.label, lineHeight: 1.6 }}>
                      The money is {ALLOCATION[r.allocation_state] ?? r.allocation_state}
                      {r.lots > 1 ? `, drawn from ${r.lots} earnings` : ""}.
                      {/* The reason is somebody's own words, so it is quoted
                          rather than run into the sentence before it. */}
                      {r.decision_reason ? ` Reason given: “${r.decision_reason}”.` : ""}
                    </p>

                    {r.cancellable ? (
                      <div>
                        <Button variant="ghost" disabled={busy === r.id}
                          onClick={() => void cancel(r)}>
                          {busy === r.id ? "Cancelling…" : "Cancel this payout"}
                        </Button>
                      </div>
                    ) : r.review_state === "SUBMITTED" || r.review_state === "UNDER_REVIEW" ? (
                      <p style={{ margin: 0, color: "var(--faint)", fontSize: fontSize.micro }}>
                        Finance has picked this up, so cancelling is now theirs to do - ask
                        support and they can.
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
      </div>
    </Card>
  );
}

/* ------------------------------------------------------ accounts (CR-11) */

function AccountsPanel({ accounts, routes, onDone }: {
  accounts: Account[] | null; routes: PayoutRoute[]; onDone: (m: string, ok?: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [routeName, setRouteName] = useState("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [replacing, setReplacing] = useState<string | null>(null);
  const [fields, setFields] = useState<ApiFieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const route = routes.find((r) => r.route === routeName);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setFields({}); setFormError(null);
    const res = await api.post<{ masked: string }>("/payout-accounts", {
      route: routeName, fields: values, supersedesId: replacing,
    });
    setBusy(false);
    if (!res.ok) {
      setFields(res.fields ?? {});
      setFormError(res.fields ? null : res.message);
      return;
    }
    setOpen(false); setValues({}); setRouteName(""); setReplacing(null);
    onDone(`Account ${res.data.masked} added. We check it before the first payout.`);
  }

  return (
    <Card padding={18}>
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "baseline", gap: space(3), flexWrap: "wrap" }}>
        <Eyebrow>Bank accounts</Eyebrow>
        {open ? null : (
          <Button variant="primary" onClick={() => setOpen(true)}>Add an account</Button>
        )}
      </div>

      <div style={{ marginTop: space(3), display: "grid", gap: space(3) }}>
        {accounts === null ? <CardListSkeleton count={2} />
          : accounts.length === 0 && !open ? (
            <StateBlock kind="empty" title="No bank account yet"
              detail="Add one and we will check it, so it is ready when you want to withdraw." />
          ) : (
            accounts.filter((a) => a.status !== "SUPERSEDED").map((a) => (
              <div key={a.id} style={{ display: "flex", justifyContent: "space-between",
                                       alignItems: "baseline", gap: space(3),
                                       flexWrap: "wrap", padding: 14, borderRadius: 10,
                                       border: "1px solid var(--line)" }}>
                <div>
                  <div style={{ fontVariantNumeric: "tabular-nums" }}>{a.masked_display}</div>
                  <div style={{ color: "var(--muted)", fontSize: fontSize.micro }}>
                    {a.beneficiary_name} · added {when(a.created_at)}
                    {a.status_reason ? ` · ${a.status_reason}` : ""}
                  </div>
                </div>
                <div style={{ display: "flex", gap: space(2), alignItems: "center" }}>
                  <Badge tone={a.status === "VERIFIED" ? "ok"
                    : a.status === "REJECTED" || a.status === "DISABLED" ? "warn" : "process"}>
                    {a.status === "PENDING_VERIFICATION" ? "Being checked"
                      : a.status === "VERIFIED" ? "Ready"
                      : a.status.toLowerCase()}
                  </Badge>
                  {a.status === "VERIFIED" || a.status === "REJECTED" ? (
                    <Button variant="ghost" onClick={() => {
                      setReplacing(a.id); setRouteName(a.route); setOpen(true);
                    }}>Replace</Button>
                  ) : null}
                </div>
              </div>
            ))
          )}

        {open ? (
          <form onSubmit={submit} style={{ display: "grid", gap: space(3), padding: 14,
                                           borderRadius: 10, background: "var(--surface-2)" }}>
            {replacing ? (
              <p style={{ margin: 0, color: "var(--muted)", fontSize: fontSize.label,
                          lineHeight: 1.6 }}>
                This replaces the account you chose. Any payout already in progress keeps
                going to the account it was approved for - replacing one never redirects
                money that is already on its way.
              </p>
            ) : null}

            <Select label="Where is the account" required value={routeName}
              onChange={(e) => { setRouteName(e.currentTarget.value); setValues({}); }}
              {...(fields["route"] ? { error: fields["route"] } : {})}>
              <option value="">Choose a country and currency</option>
              {routes.map((r) => (
                <option key={r.route} value={r.route}>{r.label}</option>
              ))}
            </Select>

            {route?.fields.map((f) => (
              <TextInput key={f.name} label={f.label} required hint={f.hint}
                value={values[f.name] ?? ""} autoComplete="off"
                onChange={(e) => setValues((v) =>
                  ({ ...v, [f.name]: e.currentTarget.value }))}
                {...(fields[f.name] ? { error: fields[f.name] } : {})} />
            ))}

            {formError ? <FormError message={formError} /> : null}

            <p style={{ margin: 0, color: "var(--faint)", fontSize: fontSize.micro,
                        lineHeight: 1.6 }}>
              We store these encrypted and show only the last four digits afterwards -
              including to our own staff, who need a specific reason and leave a record
              to see more.
            </p>

            <div style={{ display: "flex", gap: space(2) }}>
              <Button type="submit" disabled={busy || !routeName}>
                {busy ? "Saving…" : replacing ? "Replace account" : "Add account"}
              </Button>
              <Button variant="ghost" type="button" onClick={() => {
                setOpen(false); setReplacing(null); setFields({}); setFormError(null);
              }}>Cancel</Button>
            </div>
          </form>
        ) : null}
      </div>
    </Card>
  );
}
